import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  getAdditionalUserInfo,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { FirestoreDB, FirebaseAuth } from '../utils/firebase/firebase';
import {
  adminLogin,
  doctorLogin,
  organizationAdminLogin,
  patientLogin,
  schoolAdminLogin,
  studentLogin,
  teacherLogin,
} from '../api/auth';

// Utility function to safely check user role in Firestore
const safelyCheckUserRole = async (collectionName, uid) => {
  try {
    const docRef = await getDoc(doc(FirestoreDB, collectionName, uid));
    return {
      exists: docRef.exists(),
      data: docRef.exists() ? docRef.data() : null,
    };
  } catch (error) {
    console.warn(
      `Failed to check ${collectionName} for user ${uid}:`,
      error.message
    );
    return { exists: false, data: null, error: error.message };
  }
};

export const loginWithEmailPassword = async (
  email,
  password,
  setIsUserAuthenticated,
  setUser,
  setTypeOfUser,
  setIsAdminUserAuthenticated,
  adminsLoginFn,
  loginFn
) => {
  try {
    const userCredential = await signInWithEmailAndPassword(
      FirebaseAuth,
      email,
      password
    );
    const user = userCredential.user;
    const loginResult = await loginFn(
      user,
      setIsUserAuthenticated,
      setUser,
      setTypeOfUser
    );

    // Check if user is admin first
    const adminCheck = await safelyCheckUserRole('adminsList', user.uid);
    if (adminCheck.exists) {
      await adminsLoginFn(
        user,
        setIsAdminUserAuthenticated,
        setUser,
        getAdminUserFromFirestore,
        setTypeOfUser
      );
      return { status: true, data: user, userType: 'ADMIN' };
    }

    if (loginResult.status) {
      return { status: true, data: user, userType: loginResult.userType };
    } else {
      return {
        status: false,
        data: null,
        userType: null,
        message: loginResult.message,
      };
    }
  } catch (e) {
    return { status: false, data: e };
  }
};

export const loginWithGoogleProvider = async () => {
  try {
    const provider = new GoogleAuthProvider();
    provider.addScope('https://www.googleapis.com/auth/calendar.events');
    const result = await signInWithPopup(FirebaseAuth, provider);
    // eslint-disable-next-line no-unused-vars
    const credential = GoogleAuthProvider.credentialFromResult(result);
    const user = result.user;
    const moreInfo = getAdditionalUserInfo(result);
    const isNewUser = moreInfo.isNewUser;
    return { status: true, data: { ...user, isNewUser } };
  } catch (e) {
    if (typeof console !== 'undefined') {
      console.error('Google Sign-In error:', e);
    }
    console.log(e);
    return { status: false, data: e };
  }
};

export const getUserFromFirestore = async (uid, setTypeOfUser) => {
  try {
    if (!FirestoreDB) {
      throw new Error('FirestoreDB is not initialized');
    }
    if (!uid) {
      throw new Error('UID is undefined or null');
    }

    const roles = [];
    let userDetails = {};
    let accountInactiveMessage = null;
    let isTeacherExists = false;
    let isTeacherActive = false;
    let teacherData = null;
    let teacherResponse = null;
    let isSchoolAdminExists = false;
    let isSchoolAdminActive = false;
    let schoolAdminData = null;
    let schoolAdminResponse = null;

    // Check teacher status with error handling
    const teacherCheck = await safelyCheckUserRole('teachersList', uid);
    if (teacherCheck.exists) {
      isTeacherExists = true;
      teacherData = teacherCheck.data;
      if (teacherData.status === 'active') {
        isTeacherActive = true;
        teacherResponse = await teacherLogin(uid);
        userDetails = { ...userDetails, ...teacherResponse?.myDetails };
        roles.push('TEACHER');
      } else {
        roles.push('TEACHER');
        accountInactiveMessage =
          'Your account is inactive. Please contact your School Admin.';
      }
    }

    // Check school admin status with error handling
    const schoolAdminCheck = await safelyCheckUserRole('schoolAdminsList', uid);
    if (schoolAdminCheck.exists) {
      isSchoolAdminExists = true;
      schoolAdminData = schoolAdminCheck.data;
      if (schoolAdminData.status === 'active') {
        isSchoolAdminActive = true;
        schoolAdminResponse = await schoolAdminLogin(uid);
        userDetails = { ...userDetails, ...schoolAdminResponse?.myDetails };
        roles.push('SCHOOLADMIN');
      } else {
        roles.push('SCHOOLADMIN');
        if (!accountInactiveMessage) {
          accountInactiveMessage =
            'Your account is inactive. Please contact your Admin.';
        }
      }
    }

    if (isTeacherExists && isSchoolAdminExists) {
      if (isTeacherActive && !isSchoolAdminActive) {
        if (typeof setTypeOfUser === 'function') {
          setTypeOfUser('TEACHER');
        }
        return {
          status: true,
          data: teacherResponse?.myDetails || {},
          userType: 'TEACHER',
          roles: ['TEACHER'],
        };
      } else if (!isTeacherActive && isSchoolAdminActive) {
        if (typeof setTypeOfUser === 'function') {
          setTypeOfUser('SCHOOLADMIN');
        }
        return {
          status: true,
          data: schoolAdminResponse?.myDetails || {},
          userType: 'SCHOOLADMIN',
          roles: ['SCHOOLADMIN'],
        };
      }
    }

    if (roles.length > 0) {
      // Check if user has a saved role selection in localStorage
      const savedRole = localStorage.getItem('selectedRole');
      let selectedUserType;

      if (roles.length === 1) {
        // Single role user
        selectedUserType = roles[0];
      } else if (savedRole && roles.includes(savedRole)) {
        // Multi-role user with valid saved selection
        selectedUserType = savedRole;
      } else {
        // Multi-role user without valid saved selection
        selectedUserType = 'MULTIROLE';
      }

      if (typeof setTypeOfUser === 'function') {
        setTypeOfUser(selectedUserType);
      }

      return {
        status: accountInactiveMessage ? false : true,
        data: userDetails,
        userType: selectedUserType,
        roles: roles,
        ...(accountInactiveMessage && { message: accountInactiveMessage }),
      };
    }

    // Check patient status with error handling
    const patientCheck = await safelyCheckUserRole('patientsList', uid);
    if (patientCheck.exists) {
      if (typeof setTypeOfUser === 'function') {
        setTypeOfUser('PATIENT');
      }
      const response = await patientLogin(uid);
      return {
        status: true,
        data:
          {
            ...response?.myDetails,
            doctorDetails: response?.doctorDetails,
          } || {},
        userType: 'PATIENT',
        roles: ['PATIENT'],
      };
    }

    // Check doctor status with error handling
    const doctorCheck = await safelyCheckUserRole('doctorsList', uid);
    console.log('Doctor check result:', doctorCheck);
    if (doctorCheck.exists) {
      console.log('Doctor exists in Firestore, attempting doctorLogin...');
      if (typeof setTypeOfUser === 'function') {
        setTypeOfUser('DOCTOR');
      }
      try {
        const response = await doctorLogin(uid);
        console.log('Doctor login response:', response);
        if (response) {
          return {
            status: true,
            data: response?.myDetails || {},
            userType: 'DOCTOR',
            roles: ['DOCTOR'],
          };
        } else {
          console.error('Doctor login returned null/undefined response');
          return {
            status: false,
            message: 'Doctor login failed - no response data',
          };
        }
      } catch (doctorLoginError) {
        console.error('Doctor login error:', doctorLoginError);
        return {
          status: false,
          message: `Doctor login failed: ${doctorLoginError.message}`,
        };
      }
    }

    // Check student status with error handling
    const studentCheck = await safelyCheckUserRole('studentsList', uid);
    if (studentCheck.exists) {
      const studentData = studentCheck.data;
      if (studentData.status !== 'active') {
        return {
          status: false,
          message:
            'Your account is inactive. Please contact your School Admin.',
          userType: 'STUDENT',
          roles: ['STUDENT'],
        };
      }
      if (typeof setTypeOfUser === 'function') {
        setTypeOfUser('STUDENT');
      }
      const response = await studentLogin(uid);
      // Normalize possible backend typo
      const myDetails = response?.myDetails || response?.myDeatils || {};
      return {
        status: true,
        data: myDetails,
        userType: 'STUDENT',
        roles: ['STUDENT'],
      };
    }

    // Check organization admin status with error handling
    const orgAdminCheck = await safelyCheckUserRole(
      'organizationAdminsList',
      uid
    );
    if (orgAdminCheck.exists) {
      if (typeof setTypeOfUser === 'function') {
        setTypeOfUser('ORGANIZATIONADMIN');
      }
      const response = await organizationAdminLogin(uid);
      return {
        status: true,
        data: response.myDetails || {},
        userType: 'ORGANIZATIONADMIN',
        roles: ['ORGANIZATIONADMIN'],
      };
    }

    // Check preRegisteredUsers collection for users who have verified email but not moved yet
    const preRegisteredCheck = await safelyCheckUserRole(
      'preRegisteredUsers',
      uid
    );
    if (preRegisteredCheck.exists) {
      const preRegisteredData = preRegisteredCheck.data;

      // Check if the user is a student
      if (preRegisteredData.typeOfUser === 'STUDENT') {
        if (typeof setTypeOfUser === 'function') {
          setTypeOfUser('STUDENT');
        }

        // Decode the profile data from base64
        let profileData = {};
        if (preRegisteredData.profile) {
          try {
            profileData = JSON.parse(atob(preRegisteredData.profile));
          } catch (error) {
            console.error('Error decoding pre-registered user profile:', error);
          }
        }

        return {
          status: true,
          data: profileData || {},
          userType: 'STUDENT',
          roles: ['STUDENT'],
        };
      }
    }

    return { status: false, message: 'Invalid user type or not authorized.' };
  } catch (error) {
    console.error('Error fetching user from Firestore:', error);
    return { status: false, data: error.message };
  }
};

export const getAdminUserFromFirestore = async (uid, setTypeOfUser) => {
  try {
    const documentRef = await getDoc(doc(FirestoreDB, 'adminsList', uid));
    if (!documentRef.exists()) {
      return { status: false, message: 'Admin user not found' };
    }

    // Get the admin data from Firestore
    const adminData = documentRef.data();
    // Check for userType first, then role, then default to ADMIN
    const actualUserType = adminData.userType || adminData.role || 'ADMIN';

    if (typeof setTypeOfUser === 'function') {
      setTypeOfUser('ADMIN');
    }

    const response = await adminLogin(uid);
    if (!response) {
      return { status: false, message: 'Admin login failed' };
    }

    return {
      status: true,
      data: { ...response.myDetails, userType: actualUserType } || {
        userType: actualUserType,
      },
      userType: actualUserType,
      roles: ['ADMIN'],
    };
  } catch (error) {
    console.error('Error fetching admin user:', error);
    return { status: false, message: 'Error fetching admin user' };
  }
};

export const login = async (
  user,
  setIsUserAuthenticated,
  setUser,
  setTypeOfUser
) => {
  const response = await getUserFromFirestore(user?.uid, setTypeOfUser);
  if (response?.status) {
    if (typeof setIsUserAuthenticated === 'function') {
      setIsUserAuthenticated(true);
    }
    if (typeof setUser === 'function') {
      // Check if current user state has preRegisteredUserData to preserve
      const currentUserPreRegisteredData = user?.preRegisteredUserData;
      const updatedUser = {
        ...user,
        ...response.data,
        roles: response.roles,
        status: response.status,
        userType: response.userType,
        // Preserve preRegisteredUserData if it exists in the current user
        preRegisteredUserData: currentUserPreRegisteredData || null,
      };
      setUser(updatedUser);
    }
    const item = {
      value: user?.uid,
      expiry: new Date().getTime() + 3600000, // 1hr
    };
    localStorage.setItem('session', JSON.stringify(item));
    localStorage.setItem('userRole', 'user');

    // Only update selectedRole if it's not already set or if user has single role
    const currentSelectedRole = localStorage.getItem('selectedRole');
    if (!currentSelectedRole || response.roles?.length === 1) {
      localStorage.setItem('selectedRole', response.userType);
    }
    return { status: true, data: user, userType: response.userType };
  } else {
    if (typeof setIsUserAuthenticated === 'function') {
      setIsUserAuthenticated(false);
    }
    if (typeof setUser === 'function') {
      setUser(null);
    }
    localStorage.removeItem('session');
    const errorMessage =
      response?.message || 'An error occurred during login. Please try again.';
    console.error(errorMessage);
    return {
      status: false,
      data: null,
      userType: null,
      message: errorMessage,
    };
  }
};

export const adminsLogin = async (
  user,
  setIsAdminUserAuthenticated,
  setUser,
  getAdminUserFromFirestoreFn,
  setTypeOfUser
) => {
  const response = await getAdminUserFromFirestoreFn(user.uid, setTypeOfUser);
  if (response.status) {
    if (typeof setIsAdminUserAuthenticated === 'function') {
      setIsAdminUserAuthenticated(true);
    }
    if (typeof setUser === 'function') {
      setUser({
        ...user,
        ...response.data,
        roles: ['ADMIN'],
        status: 'active',
        userType: response.userType || 'ADMIN',
      });
    }
    const item = {
      value: user?.uid,
      expiry: new Date().getTime() + 3600000, // 1hr
    };
    localStorage.setItem('session', JSON.stringify(item));
    localStorage.setItem('userRole', 'admin');
    localStorage.setItem('selectedRole', 'ADMIN');
    return { status: true, data: user, userType: response.userType };
  } else {
    if (typeof setIsAdminUserAuthenticated === 'function') {
      setIsAdminUserAuthenticated(false);
    }
    if (typeof setUser === 'function') {
      setUser(null);
    }
    localStorage.removeItem('session');
    return { status: false, data: null, userType: null };
  }
};

export const logout = (
  FirebaseAuthInst,
  ACMObj,
  typeOfUser,
  setIsAdminUserAuthenticated,
  setIsUserAuthenticated,
  setUser,
  setTypeOfUser
) => {
  if (FirebaseAuthInst.currentUser) {
    FirebaseAuthInst.signOut().then(() => {
      if (ACMObj[typeOfUser]?.isAdmin) {
        if (typeof setIsAdminUserAuthenticated === 'function') {
          setIsAdminUserAuthenticated(false);
        }
      } else {
        if (typeof setIsUserAuthenticated === 'function') {
          setIsUserAuthenticated(false);
        }
      }
      if (typeof setUser === 'function') {
        setUser(null);
      }
      if (typeof setTypeOfUser === 'function') {
        setTypeOfUser(null);
      }
      localStorage.removeItem('selectedRole');
      localStorage.removeItem('userRole');
      localStorage.removeItem('session');
      localStorage.removeItem('selectedMenuKey');
      localStorage.removeItem('selectedPatientId');
    });
  }
};
